import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { EMPTY_ID } from '@e-socna/empty-client';
import { getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { ResolvableServiceListCmp_AllResolvableServices_QueryDocument } from '../../../components/ResolvableServiceList/hooks/useFindAllResolvableServicesQuery.hook';
import {
  PATH_RESOLVABLE_SERVICE_GENERAL,
  PATH_RESOLVABLE_SERVICES,
} from '../../../app/constants/paths.constant';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { ResolvableServicePage_OneResolvableService_QueryDocument } from '../../../pages/ResolvableService/hooks/useFindOneResolvableServiceQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { FormVariant } from '../../enums/variant.enum';
import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import { resolvableServiceForm2ApiMapper } from '../mappers/resolvable-service.form2api.mapper';
import type { ResolvableServiceForm_ResolvableServiceBaseInterface } from '../fragments/resolvable-service-base-interface.fragment';
import type { ResolvableServiceFormTab } from '../enums/tab.enum';

import { useCreateResolvableServiceMutation } from './useCreateResolvableServiceMutation.hook';
import { useUpdateResolvableServiceMutation } from './useUpdateResolvableServiceMutation.hook';
import type { ResolvableServiceForm_ResolvableService_Create_Result } from './useCreateResolvableServiceMutation.hook';
import type { ResolvableServiceForm_ResolvableService_Foau_Result } from './useUpdateResolvableServiceMutation.hook';

export type ResolvableServiceMutationResult =
  | ResolvableServiceForm_ResolvableService_Create_Result
  | ResolvableServiceForm_ResolvableService_Foau_Result;

interface Params {
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: ResolvableServiceMutationResult) => void;
  readonly resolvableServiceId?: Id;
}

export const useResolvableServiceSubmit = ({
  formVariant,
  handleSuccess,
  resolvableServiceId,
}: Readonly<Params>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createMutation } = useCreateResolvableServiceMutation();
  const { mutate: updateMutation } = useUpdateResolvableServiceMutation();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<
        ResolvableServiceForm_ResolvableServiceBaseInterface,
        ResolvableServiceFormTab
      >
    >) => {
      setWizardError(undefined);

      const onSuccess = (result: ResolvableServiceMutationResult) => {
        if (formVariant === FormVariant.Creation) {
          const { _id } = (
            result as ResolvableServiceForm_ResolvableService_Create_Result
          ).createResolvableService;

          enqueueSnackbar('Votre nouveau service a bien été créé.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_RESOLVABLE_SERVICE_GENERAL, { id: _id }),
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                ResolvableServiceListCmp_AllResolvableServices_QueryDocument,
              ),
            ],
          });
        } else {
          enqueueSnackbar('Votre service a bien été mis à jour.');
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                ResolvableServicePage_OneResolvableService_QueryDocument,
              ),
              { id: resolvableServiceId },
            ],
          });
        }

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(PATH_RESOLVABLE_SERVICES);
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({
          messages: userFriendlyErrors(error as BackError),
        });

        setSubmitting(false);
      };

      if (formVariant === FormVariant.Creation) {
        createMutation(
          {
            resolvableService: resolvableServiceForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else {
        updateMutation(
          {
            filter: { _id: resolvableServiceId ?? EMPTY_ID },
            update: resolvableServiceForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      buildViewSnackbarAction,
      createMutation,
      enqueueSnackbar,
      formVariant,
      handleSuccess,
      navigate,
      queryClient,
      resolvableServiceId,
      updateMutation,
    ],
  );

  return { handleSubmit };
};
